import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { tanimlarApi } from '../services/api';
import DataTable, { DtColumn } from '../components/DataTable';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const YIL = new Date().getFullYear();
const AYLAR = ['', 'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
               'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

const emptyForm = {
  isletme_id: '', yil: YIL, ay: 12, toplam_arazi_da: 0, tarla_arazisi_da: 0, otlakiye_da: 0,
  bahce_arazisi_da: 0, kulturalti_da: 0, kultur_disi_da: 0, sulanan_arazi_da: 0,
  center_pivot_da: 0, yagmurlama_da: 0, damla_sulama_da: 0, toplam_debi_lt_sn: 0, aciklama: ''
};

export default function AraziVarligi() {
  const [kayitlar,   setKayitlar]   = useState<any[]>([]);
  const [isletmeler, setIsletmeler] = useState<any[]>([]);
  const [ozet,       setOzet]       = useState<any>(null);
  const [modalAcik,  setModalAcik]  = useState(false);
  const [form,       setForm]       = useState<any>({ ...emptyForm });
  const [filtre,     setFiltre]     = useState((() => {
    const sp = new URLSearchParams(window.location.search);
    return {
      isletme_id: sp.get('isletme') || '',
      yil: Number(sp.get('yil') || YIL),
      ay: sp.get('ay') || ''
    };
  })());
  const [hata,       setHata]       = useState('');
  const [yukleniyor, setYukleniyor] = useState(false);
  const [excelMesaj, setExcelMesaj] = useState('');

  const userStr = localStorage.getItem('user');
  const user    = userStr ? JSON.parse(userStr) : null;
  const isGM    = user?.rol && ['super_admin','gm_yonetici','gm_personel'].includes(user.rol);
  const isAdmin = user?.rol && ['super_admin','gm_yonetici'].includes(user.rol);

  const [kiralikGoster, setKiralikGoster] = useState(false);

  useEffect(() => {
    if (!isGM && user?.isletme_id) setFiltre(f => ({ ...f, isletme_id: user.isletme_id }));
  }, []);

  useEffect(() => {
    tanimlarApi.isletmelerListesi(kiralikGoster).then(r => setIsletmeler(r.data));
  }, [kiralikGoster]);

  useEffect(() => { yukle(); }, [filtre]);

  const yukle = () => {
    const p: any = { yil: filtre.yil };
    if (filtre.isletme_id) p.isletme_id = filtre.isletme_id;
    if (filtre.ay) p.ay = Number(filtre.ay);
    axios.get(`${API}/api/varlik/arazi`, { headers: headers(), params: p }).then(r => setKayitlar(r.data)).catch(() => setKayitlar([]));
    axios.get(`${API}/api/varlik/ozet`,  { headers: headers(), params: p }).then(r => setOzet(r.data)).catch(() => setOzet(null));
  };

  const mevcuduYukle = async (isletme_id: any, yil: number) => {
    if (!isletme_id) return;
    try {
      const r = await axios.get(`${API}/api/varlik/arazi`, { headers: headers(), params: { isletme_id, yil } });
      const mevcut = r.data?.[0];
      if (mevcut) setForm({ ...mevcut });
    } catch {}
  };

  const kaydet = async () => {
    if (!form.isletme_id) { setHata('İşletme seçiniz'); return; }
    setHata(''); setYukleniyor(true);
    try {
      if (form.id) {
        await axios.put(`${API}/api/varlik/arazi/${form.id}`, { ...form, isletme_id: Number(form.isletme_id) }, { headers: headers() });
      } else {
        await axios.post(`${API}/api/varlik/arazi`, { ...form, isletme_id: Number(form.isletme_id) }, { headers: headers() });
      }
      yukle(); setModalAcik(false); setForm({ ...emptyForm });
    } catch (e: any) { setHata(e.response?.data?.detail || 'Hata oluştu'); }
    finally { setYukleniyor(false); }
  };

  const sil = async (id: number) => {
    if (!window.confirm('Silmek istiyor musunuz?')) return;
    try { await axios.delete(`${API}/api/varlik/arazi/${id}`, { headers: headers() }); yukle(); } catch {}
  };

  const excelYukle = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    setExcelMesaj('Yükleniyor...');
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await axios.post(`${API}/api/varlik/arazi/excel-import`, fd, { headers: headers() });
      setExcelMesaj(`✅ ${res.data.basarili} kayıt aktarıldı`); yukle();
    } catch { setExcelMesaj('❌ Hata'); }
    e.target.value = '';
  };

  const silSecili = async (rows: any[]) => {
    for (const k of rows) await axios.delete(`${API}/api/varlik/arazi/${k.id}`, { headers: headers() });
    yukle();
  };

  const n = (v: any) => Number(v || 0).toLocaleString('tr-TR');

  const araziColumns: DtColumn[] = [
    { key: 'isletme', label: 'İşletme', sortable: true, filterable: true, render: (k: any) => <><strong>{k.isletme_kod}</strong><br /><small>{k.isletme_ad}</small></>, sortValue: (k: any) => k.isletme_kod || '', filterValue: (k: any) => k.isletme_ad || '', exportValue: (k: any) => `${k.isletme_kod} ${k.isletme_ad}` },
    { key: 'yil', label: 'Yıl', sortable: true, filterable: true, sortValue: (k: any) => Number(k.yil), filterValue: (k: any) => String(k.yil) },
    { key: 'ay', label: 'Ay', sortable: true, filterable: true, render: (k: any) => AYLAR[k.ay] || k.ay, filterValue: (k: any) => AYLAR[k.ay] || String(k.ay), sortValue: (k: any) => Number(k.ay) },
    { key: 'toplam_arazi_da', label: 'Toplam (da)', sortable: true, render: (k: any) => <strong>{n(k.toplam_arazi_da)}</strong>, sortValue: (k: any) => Number(k.toplam_arazi_da || 0), exportValue: (k: any) => String(k.toplam_arazi_da || 0) },
    { key: 'tarla_arazisi_da', label: 'Tarla (da)', sortable: true, render: (k: any) => n(k.tarla_arazisi_da), sortValue: (k: any) => Number(k.tarla_arazisi_da || 0) },
    { key: 'otlakiye_da', label: 'Otlakiye (da)', sortable: true, render: (k: any) => n(k.otlakiye_da), sortValue: (k: any) => Number(k.otlakiye_da || 0) },
    { key: 'bahce_arazisi_da', label: 'Bahçe (da)', sortable: true, render: (k: any) => n(k.bahce_arazisi_da), sortValue: (k: any) => Number(k.bahce_arazisi_da || 0) },
    { key: 'kulturalti_da', label: 'Kültür Altı', sortable: true, render: (k: any) => n(k.kulturalti_da), sortValue: (k: any) => Number(k.kulturalti_da || 0) },
    { key: 'kultur_disi_da', label: 'Kültür Dışı', sortable: true, render: (k: any) => n(k.kultur_disi_da), sortValue: (k: any) => Number(k.kultur_disi_da || 0) },
    { key: 'sulanan_arazi_da', label: 'Sulanan (da)', sortable: true, render: (k: any) => <strong style={{ color: '#2980b9' }}>{n(k.sulanan_arazi_da)}</strong>, sortValue: (k: any) => Number(k.sulanan_arazi_da || 0) },
    { key: 'center_pivot_da', label: 'C.Pivot (da)', sortable: true, render: (k: any) => n(k.center_pivot_da), sortValue: (k: any) => Number(k.center_pivot_da || 0) },
    { key: 'yagmurlama_da', label: 'Yağmurlama', sortable: true, render: (k: any) => n(k.yagmurlama_da), sortValue: (k: any) => Number(k.yagmurlama_da || 0) },
    { key: 'damla_sulama_da', label: 'Damla (da)', sortable: true, render: (k: any) => n(k.damla_sulama_da), sortValue: (k: any) => Number(k.damla_sulama_da || 0) },
    { key: 'islem', label: 'İşlem', noExport: true, render: (k: any) => (
      <div style={{ display: 'flex', gap: 4 }}>
        {isAdmin && <button className="btn btn-primary btn-sm" onClick={() => { setForm({ ...k }); setHata(''); setModalAcik(true); }}>Düzenle</button>}
        <button className="btn btn-danger btn-sm" onClick={() => sil(k.id)}>Sil</button>
      </div>
    )},
  ];

  const inp = (field: string) => ({
    className: "form-control", type: "number" as const, value: form[field],
    onChange: (e: any) => setForm({ ...form, [field]: Number(e.target.value) })
  });

  return (
    <div>
      <div className="page-header">
        <h1>🌱 Arazi Varlığı</h1>
        <p>İşletme bazında yıllık arazi varlığı verileri</p>
      </div>

      {ozet && (
        <div className="stats-grid">
          <div className="stat-card"><div className="stat-icon">🌱</div><div><div className="stat-value">{n(ozet.toplam_arazi_da)}</div><div className="stat-label">Toplam Arazi (da)</div></div></div>
          <div className="stat-card"><div className="stat-icon">💧</div><div><div className="stat-value">{n(ozet.sulanan_arazi_da)}</div><div className="stat-label">Sulanan Arazi (da)</div></div></div>
          <div className="stat-card"><div className="stat-icon">🌾</div><div><div className="stat-value">{n(ozet.tarla_arazisi_da)}</div><div className="stat-label">Tarla Arazisi (da)</div></div></div>
          <div className="stat-card"><div className="stat-icon">🌿</div><div><div className="stat-value">{n(ozet.otlakiye_da)}</div><div className="stat-label">Otlakiye (da)</div></div></div>
        </div>
      )}

      <div className="card">
        <div className="toolbar">
          <div style={{ display: 'flex', gap: 8 }}>
            <input type="number" className="search-input" style={{ width: 90 }} value={filtre.yil}
              onChange={e => setFiltre({ ...filtre, yil: Number(e.target.value) })} placeholder="Yıl" />
            <select className="search-input" style={{ width: 120 }} value={filtre.ay}
              onChange={e => setFiltre({ ...filtre, ay: e.target.value })}>
              <option value="">Tüm Aylar</option>
              {AYLAR.slice(1).map((a, i) => <option key={i + 1} value={i + 1}>{a}</option>)}
            </select>
            {isGM && (
              <>
                <select className="search-input" style={{ width: 210 }} value={filtre.isletme_id}
                  onChange={e => setFiltre({ ...filtre, isletme_id: e.target.value })}>
                  <option value="">Tüm İşletmeler</option>
                  {isletmeler.map(i => <option key={i.id} value={i.id}>{i.ad}</option>)}
                </select>
                <label style={{ display:'flex', alignItems:'center', gap:4, fontSize:13, cursor:'pointer', whiteSpace:'nowrap' }}>
                  <input type="checkbox" checked={kiralikGoster} onChange={e => setKiralikGoster(e.target.checked)} />
                  Kiralıkları Göster
                </label>
              </>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {excelMesaj && <span style={{ fontSize: 12, color: '#555' }}>{excelMesaj}</span>}
            <label className="btn" style={{ background: '#2ecc71', color: 'white', cursor: 'pointer' }}>
              📥 Excel Yükle <input type="file" accept=".xlsx" style={{ display: 'none' }} onChange={excelYukle} />
            </label>
            <button className="btn btn-success" onClick={() => { setForm({ ...emptyForm, isletme_id: isGM ? '' : user?.isletme_id }); setHata(''); setModalAcik(true); }}>+ Yeni Kayıt</button>
          </div>
        </div>
        <DataTable columns={araziColumns} data={kayitlar} rowKey={(k: any) => k.id} onDeleteSelected={silSecili} exportFilename="arazi-varligi" />
      </div>

      {modalAcik && (
        <div className="modal-overlay" onClick={() => setModalAcik(false)}>
          <div className="modal" style={{ width: 600, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3>🌱 Arazi Varlığı Kayıt Girişi</h3><button className="modal-close" onClick={() => setModalAcik(false)}>×</button></div>
            <div className="modal-body">
              {hata && <div className="alert alert-danger">{hata}</div>}
              <div className="form-row">
                {isGM && (
                  <div className="form-group"><label>İşletme *</label>
                    <select className="form-control" value={form.isletme_id} onChange={e => {
                      const yeniId = e.target.value;
                      setForm({ ...emptyForm, isletme_id: yeniId, yil: form.yil });
                      mevcuduYukle(yeniId, form.yil);
                    }}>
                      <option value="">Seçiniz</option>
                      {isletmeler.map(i => <option key={i.id} value={i.id}>{i.ad}</option>)}
                    </select>
                  </div>
                )}
                <div className="form-group"><label>Yıl *</label><input className="form-control" type="number" value={form.yil} onChange={e => {
                  const yeniYil = Number(e.target.value);
                  setForm({ ...emptyForm, isletme_id: form.isletme_id, yil: yeniYil, ay: form.ay });
                  mevcuduYukle(form.isletme_id, yeniYil);
                }} /></div>
                <div className="form-group"><label>Ay *</label>
                  <select className="form-control" value={form.ay} onChange={e => setForm({ ...form, ay: Number(e.target.value) })}>
                    {AYLAR.slice(1).map((a, i) => <option key={i + 1} value={i + 1}>{a}</option>)}
                  </select>
                </div>
              </div>
              <p style={{ fontWeight: 'bold', fontSize: 13, color: '#1a5276', margin: '8px 0 6px' }}>🌱 Arazi Varlığı</p>
              <div className="form-row">
                <div className="form-group"><label>Toplam Arazi (da)</label><input {...inp('toplam_arazi_da')} /></div>
                <div className="form-group"><label>Tarla Arazisi (da)</label><input {...inp('tarla_arazisi_da')} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Otlakiye (da)</label><input {...inp('otlakiye_da')} /></div>
                <div className="form-group"><label>Bahçe Arazisi (da)</label><input {...inp('bahce_arazisi_da')} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Kültür Altı (da)</label><input {...inp('kulturalti_da')} /></div>
                <div className="form-group"><label>Kültür Dışı (da)</label><input {...inp('kultur_disi_da')} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Sulanan Arazi (da)</label><input {...inp('sulanan_arazi_da')} /></div>
              </div>
              <p style={{ fontWeight: 'bold', fontSize: 13, color: '#1a5276', margin: '8px 0 6px' }}>💧 Sulama Sistemleri</p>
              <div className="form-row">
                <div className="form-group"><label>Center Pivot (da)</label><input {...inp('center_pivot_da')} /></div>
                <div className="form-group"><label>Yağmurlama (da)</label><input {...inp('yagmurlama_da')} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Damla Sulama (da)</label><input {...inp('damla_sulama_da')} /></div>
                <div className="form-group"><label>Toplam Debi (lt/sn)</label><input {...inp('toplam_debi_lt_sn')} /></div>
              </div>
              <div className="form-group"><label>Açıklama</label><textarea className="form-control" value={form.aciklama} onChange={e => setForm({ ...form, aciklama: e.target.value })} rows={2} /></div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setModalAcik(false)} style={{ background: '#eee' }}>İptal</button>
              <button className="btn btn-primary" onClick={kaydet} disabled={yukleniyor}>{yukleniyor ? 'Kaydediliyor...' : 'Kaydet'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
